import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, AlertCircle, Info, Check } from "lucide-react";

export interface Country {
  code: string;
  name: string;
  flag: string;
  hidden?: boolean; // Hidden countries only appear in search
}

// Main visible countries
export const COUNTRIES: Country[] = [
  // Visible countries (shown in scroll list)
  { code: "FR", name: "France", flag: "🇫🇷" },
  { code: "DZ", name: "Algérie", flag: "🇩🇿" },
  { code: "MA", name: "Maroc", flag: "🇲🇦" },
  { code: "TN", name: "Tunisie", flag: "🇹🇳" },
  { code: "IT", name: "Italie", flag: "🇮🇹" },
  { code: "PT", name: "Portugal", flag: "🇵🇹" },
  { code: "ES", name: "Espagne", flag: "🇪🇸" },
  { code: "SN", name: "Sénégal", flag: "🇸🇳" },
  { code: "CD", name: "Congo", flag: "🇨🇩" },
  { code: "EG", name: "Égypte", flag: "🇪🇬" },
  { code: "PS", name: "Palestine", flag: "🇵🇸" },
  { code: "IQ", name: "Irak", flag: "🇮🇶" },
  { code: "IR", name: "Iran", flag: "🇮🇷" },
  { code: "SA", name: "Arabie Saoudite", flag: "🇸🇦" },
  { code: "AE", name: "Émirats Arabes Unis", flag: "🇦🇪" },
  { code: "QA", name: "Qatar", flag: "🇶🇦" },
  { code: "PL", name: "Pologne", flag: "🇵🇱" },
  { code: "AL", name: "Albanie", flag: "🇦🇱" },
  { code: "GB", name: "Royaume-Uni", flag: "🇬🇧" },
  { code: "US", name: "États-Unis", flag: "🇺🇸" },
  { code: "BE", name: "Belgique", flag: "🇧🇪" },
  { code: "CH", name: "Suisse", flag: "🇨🇭" },
  { code: "DE", name: "Allemagne", flag: "🇩🇪" },
  { code: "NL", name: "Pays-Bas", flag: "🇳🇱" },
  { code: "CA", name: "Canada", flag: "🇨🇦" },
  
  // Hidden countries (only appear via search)
  { code: "IS", name: "Islande", flag: "🇮🇸", hidden: true },
  { code: "SE", name: "Suède", flag: "🇸🇪", hidden: true },
  { code: "NO", name: "Norvège", flag: "🇳🇴", hidden: true },
  { code: "FI", name: "Finlande", flag: "🇫🇮", hidden: true },
  { code: "RO", name: "Roumanie", flag: "🇷🇴", hidden: true },
  { code: "RU", name: "Russie", flag: "🇷🇺", hidden: true },
  { code: "IE", name: "Irlande", flag: "🇮🇪", hidden: true },
  { code: "DK", name: "Danemark", flag: "🇩🇰", hidden: true },
  { code: "MT", name: "Malte", flag: "🇲🇹", hidden: true },
  { code: "AT", name: "Autriche", flag: "🇦🇹", hidden: true },
  { code: "GR", name: "Grèce", flag: "🇬🇷", hidden: true },
  { code: "CY", name: "Chypre", flag: "🇨🇾", hidden: true },
  { code: "CZ", name: "République tchèque", flag: "🇨🇿", hidden: true },
  { code: "UA", name: "Ukraine", flag: "🇺🇦", hidden: true },
  { code: "GE", name: "Géorgie", flag: "🇬🇪", hidden: true },
  { code: "EE", name: "Estonie", flag: "🇪🇪", hidden: true },
  { code: "AZ", name: "Azerbaïdjan", flag: "🇦🇿", hidden: true },
  { code: "HR", name: "Croatie", flag: "🇭🇷", hidden: true },
  { code: "SI", name: "Slovénie", flag: "🇸🇮", hidden: true },
  { code: "ME", name: "Monténégro", flag: "🇲🇪", hidden: true },
  { code: "MK", name: "Macédoine du Nord", flag: "🇲🇰", hidden: true },
  { code: "RS", name: "Serbie", flag: "🇷🇸", hidden: true },
  { code: "BA", name: "Bosnie-Herzégovine", flag: "🇧🇦", hidden: true },
  { code: "HU", name: "Hongrie", flag: "🇭🇺", hidden: true },
  { code: "LV", name: "Lettonie", flag: "🇱🇻", hidden: true },
  { code: "LT", name: "Lituanie", flag: "🇱🇹", hidden: true },
  { code: "LY", name: "Libye", flag: "🇱🇾", hidden: true },
  { code: "SD", name: "Soudan", flag: "🇸🇩", hidden: true },
  { code: "MR", name: "Mauritanie", flag: "🇲🇷", hidden: true },
  { code: "SO", name: "Somalie", flag: "🇸🇴", hidden: true },
  { code: "KW", name: "Koweït", flag: "🇰🇼", hidden: true },
  { code: "BH", name: "Bahreïn", flag: "🇧🇭", hidden: true },
  { code: "OM", name: "Oman", flag: "🇴🇲", hidden: true },
  { code: "YE", name: "Yémen", flag: "🇾🇪", hidden: true },
  { code: "JO", name: "Jordanie", flag: "🇯🇴", hidden: true },
  { code: "LB", name: "Liban", flag: "🇱🇧", hidden: true },
  { code: "SY", name: "Syrie", flag: "🇸🇾", hidden: true },
  { code: "KRD", name: "Kurdistan", flag: "🏴", hidden: true },
  { code: "IN", name: "Inde", flag: "🇮🇳", hidden: true },
  { code: "BD", name: "Bangladesh", flag: "🇧🇩", hidden: true },
  { code: "AF", name: "Afghanistan", flag: "🇦🇫", hidden: true },
  { code: "PH", name: "Philippines", flag: "🇵🇭", hidden: true },
  { code: "TH", name: "Thaïlande", flag: "🇹🇭", hidden: true },
  { code: "VN", name: "Vietnam", flag: "🇻🇳", hidden: true },
  { code: "MY", name: "Malaisie", flag: "🇲🇾", hidden: true },
  { code: "KR", name: "Corée du Sud", flag: "🇰🇷", hidden: true },
  { code: "JP", name: "Japon", flag: "🇯🇵", hidden: true },
  { code: "TW", name: "Taïwan", flag: "🇹🇼", hidden: true },
  { code: "CN", name: "Chine", flag: "🇨🇳", hidden: true },
  { code: "ID", name: "Indonésie", flag: "🇮🇩", hidden: true },
  { code: "LK", name: "Sri Lanka", flag: "🇱🇰", hidden: true },
  { code: "BR", name: "Brésil", flag: "🇧🇷", hidden: true },
  { code: "AR", name: "Argentine", flag: "🇦🇷", hidden: true },
  { code: "CL", name: "Chili", flag: "🇨🇱", hidden: true },
  { code: "PE", name: "Pérou", flag: "🇵🇪", hidden: true },
  { code: "CO", name: "Colombie", flag: "🇨🇴", hidden: true },
  { code: "EC", name: "Équateur", flag: "🇪🇨", hidden: true },
  { code: "VE", name: "Venezuela", flag: "🇻🇪", hidden: true },
  { code: "CU", name: "Cuba", flag: "🇨🇺", hidden: true },
  { code: "MX", name: "Mexique", flag: "🇲🇽", hidden: true },
  { code: "PA", name: "Panama", flag: "🇵🇦", hidden: true },
  { code: "SV", name: "Salvador", flag: "🇸🇻", hidden: true },
  { code: "PY", name: "Paraguay", flag: "🇵🇾", hidden: true },
  { code: "UY", name: "Uruguay", flag: "🇺🇾", hidden: true },
  { code: "CB", name: "Caraïbes", flag: "🌴", hidden: true },
  { code: "AU", name: "Australie", flag: "🇦🇺", hidden: true },
];

const MAX_COUNTRIES = 10;
const RECOMMENDED_MAX = 6;

interface CountrySelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (countries: string[]) => void;
  title?: string;
}

const CountrySelectionModal = ({
  open,
  onOpenChange,
  onConfirm,
  title = "Sélectionnez vos pays",
}: CountrySelectionModalProps) => {
  const [search, setSearch] = useState("");
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);

  const filteredCountries = useMemo(() => {
    const searchTrimmed = search.trim();
    
    if (!searchTrimmed) {
      // No search: show only visible (non-hidden) countries
      return COUNTRIES.filter((country) => !country.hidden);
    }
    
    // With search: search ALL countries (including hidden ones)
    const searchLower = searchTrimmed.toLowerCase();
    return COUNTRIES.filter(
      (country) =>
        country.name.toLowerCase().includes(searchLower) ||
        country.code.toLowerCase().includes(searchLower)
    );
  }, [search]);

  const toggleCountry = (code: string) => {
    setSelectedCountries((prev) => {
      if (prev.includes(code)) {
        return prev.filter((c) => c !== code);
      }
      if (prev.length >= MAX_COUNTRIES) {
        return prev;
      }
      return [...prev, code];
    });
  };

  const handleConfirm = () => {
    if (selectedCountries.length === 0) return;
    onConfirm(selectedCountries);
    // Reset state
    setSelectedCountries([]);
    setSearch("");
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset state after animation
    setTimeout(() => {
      setSelectedCountries([]);
      setSearch("");
    }, 200);
  };

  const getCountryName = (code: string) => {
    return COUNTRIES.find((c) => c.code === code)?.name || code;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent 
        className="bg-card border-border text-foreground max-w-lg w-[95vw] max-h-[90vh] p-0 flex flex-col overflow-hidden"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="p-4 pb-0 flex-shrink-0">
          <DialogTitle className="text-xl">{title}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col flex-1 overflow-hidden p-4 pt-2">
          {/* Search bar - no autofocus */}
          <div className="relative mb-3 flex-shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher parmi +90 pays disponibles…"
              className="pl-10 bg-input border-border"
              autoFocus={false}
              autoComplete="off"
            />
          </div>

          {/* Recommendation info */}
          <div className="flex items-start gap-2 p-3 mb-3 rounded-lg bg-primary/10 border border-primary/20 flex-shrink-0">
            <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <p className="text-sm text-foreground/80">
              💡 Pour une qualité optimale, nous recommandons de sélectionner{" "}
              <strong className="text-primary">5 à 6 pays maximum</strong>.
            </p>
          </div>

          {/* Warning when exceeding recommended */}
          {selectedCountries.length >= RECOMMENDED_MAX && selectedCountries.length < MAX_COUNTRIES && (
            <div className="flex items-start gap-2 p-3 mb-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-200">
                Vous avez atteint la limite recommandée ({RECOMMENDED_MAX} pays). 
                Vous pouvez encore en ajouter {MAX_COUNTRIES - selectedCountries.length}.
              </p>
            </div>
          )}

          {/* Max limit warning */}
          {selectedCountries.length >= MAX_COUNTRIES && (
            <div className="flex items-start gap-2 p-3 mb-3 rounded-lg bg-destructive/10 border border-destructive/30 flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <p className="text-sm text-destructive">
                Limite maximum atteinte ({MAX_COUNTRIES} pays). 
                Désélectionnez un pays pour en ajouter un autre.
              </p>
            </div>
          )}

          {/* Selection counter */}
          <div className="flex items-center justify-between mb-2 text-sm flex-shrink-0">
            <span className="text-muted-foreground">
              {selectedCountries.length} pays sélectionné{selectedCountries.length > 1 ? "s" : ""}
            </span>
            {selectedCountries.length > 0 && (
              <button
                onClick={() => setSelectedCountries([])}
                className="text-primary hover:underline text-sm"
              >
                Tout désélectionner
              </button>
            )}
          </div>

          {/* Countries list - scrollable with native scroll */}
          <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
            <div className="space-y-1 pb-2">
              {filteredCountries.map((country) => {
                const isSelected = selectedCountries.includes(country.code);
                const isDisabled = !isSelected && selectedCountries.length >= MAX_COUNTRIES;

                return (
                  <button
                    key={country.code}
                    onClick={() => !isDisabled && toggleCountry(country.code)}
                    disabled={isDisabled}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left ${
                      isSelected
                        ? "bg-primary/20 border border-primary/50"
                        : isDisabled
                        ? "bg-muted/30 opacity-50 cursor-not-allowed"
                        : "bg-muted/50 hover:bg-muted border border-transparent"
                    }`}
                  >
                    <span className="text-2xl">{country.flag}</span>
                    <span className="flex-1 font-medium text-foreground">
                      {country.name}
                    </span>
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                      isSelected 
                        ? "bg-primary border-primary" 
                        : "border-muted-foreground/50"
                    }`}>
                      {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                    </div>
                  </button>
                );
              })}

              {filteredCountries.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  Aucun pays trouvé pour "{search}"
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Fixed footer with button - always visible */}
        <div className="flex-shrink-0 p-4 pt-2 border-t border-border bg-card">
          {/* Selected countries summary */}
          {selectedCountries.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-muted-foreground mb-2">Pays sélectionnés :</p>
              <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto">
                {selectedCountries.map((code) => (
                  <span
                    key={code}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/20 text-xs text-foreground"
                  >
                    {COUNTRIES.find((c) => c.code === code)?.flag} {getCountryName(code)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Confirm button - always visible */}
          <Button
            onClick={handleConfirm}
            disabled={selectedCountries.length === 0}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-5"
          >
            Valider ma sélection ({selectedCountries.length} pays)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CountrySelectionModal;

// Helper function to get country names from codes
export const getCountryNames = (codes: string[]): string => {
  return codes
    .map((code) => COUNTRIES.find((c) => c.code === code)?.name || code)
    .join(", ");
};
